import * as React from 'react';
import './skeleton.css';

/**
 * Skeleton (CC8): a teal-tinted shimmer block drawn at the final height of the
 * content it stands in for. Same `style`/`className` API as before, so every
 * existing call site keeps working; the shimmer moved from an injected
 * <style> tag into skeleton.css so it can sit behind prefers-reduced-motion.
 *
 *   <Skeleton style={{ width: 240, height: 52 }} />
 *   <Skeleton variant="text" width="80%" />
 *   <Skeleton variant="tile" height={220} />
 *
 * Variants only change the radius: text (7px), title (9px), pill (999px),
 * tile (20px), card (28px). Default is the 14px field radius.
 */
export type SkeletonVariant = 'text' | 'title' | 'pill' | 'tile' | 'card';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
}

export function Skeleton({
  className = '',
  style,
  variant,
  width,
  height,
  ...rest
}: SkeletonProps) {
  const cls = ['lnr-skeleton', variant ? `lnr-skeleton--${variant}` : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      aria-hidden="true"
      className={cls}
      style={{
        ...(width !== undefined ? { width } : null),
        ...(height !== undefined ? { height } : null),
        ...style,
      }}
      {...rest}
    />
  );
}

/**
 * A card-shaped stage for skeleton content (same chrome as .lnr-card), so the
 * loading frame already has the final frame's edges and shadow.
 */
export function SkeletonPanel({
  className = '',
  tile = false,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { tile?: boolean }) {
  return (
    <div
      aria-busy="true"
      className={['lnr-skeleton-panel', tile ? 'lnr-skeleton-panel--tile' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

/** A few text lines, widths tapering so the block reads as a paragraph. */
export function SkeletonLines({
  lines = 3,
  style,
}: {
  lines?: number;
  style?: React.CSSProperties;
}) {
  const widths = ['92%', '78%', '64%', '86%', '52%'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} variant="text" width={widths[i % widths.length]} />
      ))}
    </div>
  );
}

export default Skeleton;
